import { VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  hasComposerContent,
  useDocumentChatComposer,
} from './document-chat-composer-context';
import { DocumentComposerModelPicker } from './document-composer-model-picker';
import { useEditDocument } from './edit-document-context';
import { stringifyEditorJs } from './editorjs-types';
import { usePromptSubmit } from './prompt-command';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { usePageFooter } from '#src/providers/page-layout-provider';

export interface DocumentChatFooterProps {
  documentId: string | undefined;
  isNewDocument: boolean;
  /** When false, the AI composer is not mounted in the footer and layout uses float mode. */
  active?: boolean;
}

/**
 * Document AI chat: portals into the app shell footer ([`PageFooter`](../../components/page-footer.tsx))
 * like [`Dashboard`](../../pages/dashboard.tsx), using `usePageFooter` + `createPortal`.
 * The app shell keeps header and footer in view; only the main column scrolls.
 */
export function DocumentChatFooter({
  documentId,
  isNewDocument,
  active = true,
}: DocumentChatFooterProps) {
  const { footerRef } = usePageFooter();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) {
      setPortalTarget(null);
      return () => {
        setPortalTarget(null);
      };
    }

    let cancelled = false;
    const tryAttach = (attempt: number) => {
      if (cancelled) return;
      if (footerRef.current) {
        setPortalTarget(footerRef.current);
        return;
      }
      if (attempt > 50) return;
      setTimeout(() => tryAttach(attempt + 1), 0);
    };
    tryAttach(0);

    return () => {
      cancelled = true;
      setPortalTarget(null);
    };
  }, [active, footerRef]);

  const { setValue, getValue } = useEditDocument();
  const {
    promptText,
    setPromptText,
    clear,
    composerRootRef,
    sessionComposerModel,
    setSessionComposerModel,
    composerModelOptions,
  } = useDocumentChatComposer();
  const { handleSubmit, disabled, processingPrompt } = usePromptSubmit({
    documentId,
    isNewDocument,
    focusBlockId: undefined,
    onValueUpdate: setValue,
    getEditorSnapshot: () => stringifyEditorJs(getValue()),
    model: sessionComposerModel,
  });

  const onApply = useCallback(async () => {
    if (!hasComposerContent(promptText)) return;
    const wire = promptText.trim();
    if (!wire) return;
    const ok = await handleSubmit(wire);
    if (ok) clear();
  }, [promptText, clear, handleSubmit]);

  const footerContent = (
    <VStack gap={2} align="stretch" width="100%">
      <ChatComposerRow
        rootRef={composerRootRef}
        value={promptText}
        onChange={setPromptText}
        onSubmit={onApply}
        placeholder="Describe the change…"
        disabled={disabled}
        loading={processingPrompt}
        trailingActions={
          <DocumentComposerModelPicker
            value={sessionComposerModel}
            onValueChange={setSessionComposerModel}
            options={composerModelOptions}
            disabled={disabled}
          />
        }
      />
    </VStack>
  );

  if (!active || !portalTarget) return null;

  return createPortal(footerContent, portalTarget);
}
